import { useState, useEffect, useCallback } from "react";
import HelpButton from "../components/HelpButton";
import HelpFieldIcon from "../components/HelpFieldIcon";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { UserPlus, Trash, IdentificationCard, X, ArrowsLeftRight, UsersThree } from "@phosphor-icons/react";
import ManageTeamModal from "../components/ManageTeamModal";

// Default role catalog — covers every legacy + extended role.
// At runtime we ALSO union this with any role found in users[] (backward-compat for custom roles)
// and any role returned by /api/roles/permissions (system role master).
const DEFAULT_ROLES = [
  "admin", "md", "hr", "manager", "bdm", "bde",
  "operations", "store", "purchase", "digital_marketing",
  "field_engineer", "delivery_boy", "customer",
];

const UserManagement = ({ user, logout }) => {
  const [users, setUsers] = useState([]);
  const [systemRoles, setSystemRoles] = useState([]);  // dynamic list from backend
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "bde" });
  const [departments, setDepartments] = useState([]);
  const [orgRoles, setOrgRoles] = useState([]);
  const [orgUser, setOrgUser] = useState(null);  // user being org-edited
  const [orgPatch, setOrgPatch] = useState({});
  const [transferFrom, setTransferFrom] = useState(null);  // user being transferred out
  const [transferToId, setTransferToId] = useState("");
  const [transferPreview, setTransferPreview] = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferExecuting, setTransferExecuting] = useState(false);
  const [transferResult, setTransferResult] = useState(null);
  const [manageTeamBdm, setManageTeamBdm] = useState(null);  // {bdm_user_id, bdm_name} being managed

  // Computed roles list: default ∪ system-master ∪ existing-user-roles. Stable order, lower-cased.
  const ROLES = (() => {
    const set = new Set(DEFAULT_ROLES);
    systemRoles.forEach((r) => r && set.add(String(r).toLowerCase()));
    users.forEach((u) => u?.role && set.add(String(u.role).toLowerCase()));
    // Preserve canonical order for the known ones, then append any extras alphabetically
    const known = DEFAULT_ROLES.filter((r) => set.has(r));
    const extras = [...set].filter((r) => !DEFAULT_ROLES.includes(r)).sort();
    return [...known, ...extras];
  })();

  const fetchUsers = useCallback(async () => {
    try {
      const [u, d, r, sysRoles] = await Promise.all([
        api.get(`/users`),
        axios.get(`${API}/departments`).catch(() => ({ data: [] })),
        axios.get(`${API}/org-roles`).catch(() => ({ data: [] })),
        // System role master — drives the dynamic Change Role dropdown
        api.get(`${API}/roles/permissions`).catch(() => ({ data: { hierarchy: {} } })),
      ]);
      setUsers(u.data);
      setDepartments(d.data || []);
      setOrgRoles(r.data || []);
      // /api/roles/permissions returns { hierarchy: {role_key: level, ...}, roles: {...} }
      const hier = sysRoles.data?.hierarchy || {};
      setSystemRoles(Object.keys(hier));
    } catch (err) { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, newUser);
      toast.success(`User ${newUser.name} created as ${newUser.role.toUpperCase()}`);
      setShowAdd(false);
      setNewUser({ email: "", name: "", password: "", role: "bde" });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    }
  };

  const changeRole = async (email, newRole) => {
    try {
      await api.put(`/users/${email}/role?role=${newRole}`);
      toast.success(`Role updated to ${newRole.toUpperCase()}`);
      fetchUsers();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to update role"); }
  };

  const deleteUser = async (u) => {
    if (u.email === "admin@swiftechindia.com") {
      toast.error("Cannot delete primary admin");
      return;
    }
    if (!window.confirm(`Delete user "${u.name}"? All their CRM data will remain but they cannot log in anymore.`)) return;
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success(`${u.name} deleted`);
      fetchUsers();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const openOrg = (u) => {
    setOrgUser(u);
    setOrgPatch({
      employee_id: u.employee_id || "",
      department_id: u.department_id || "",
      department_name: u.department_name || "",
      org_role_id: u.org_role_id || "",
      org_role_name: u.org_role_name || "",
      reporting_manager_id: u.reporting_manager_id || "",
      reporting_manager_name: u.reporting_manager_name || "",
      mobile: u.mobile || "",
      whatsapp: u.whatsapp || "",
      branch: u.branch || "",
      status: u.status || "active",
      login_access: u.login_access !== false,
    });
  };

  const saveOrg = async () => {
    try {
      await api.put(`/users/${orgUser.user_id}/org`, orgPatch);
      toast.success("Org details updated");
      setOrgUser(null);
      fetchUsers();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const openTransfer = (u) => {
    setTransferFrom(u);
    setTransferToId("");
    setTransferPreview(null);
    setTransferResult(null);
  };

  const closeTransfer = () => {
    setTransferFrom(null);
    setTransferToId("");
    setTransferPreview(null);
    setTransferResult(null);
  };

  const runTransferPreview = async () => {
    if (!transferToId) {
      toast.error("Select the replacement employee first");
      return;
    }
    setTransferLoading(true);
    setTransferPreview(null);
    try {
      const res = await api.get(`/admin/employee-transfer/preview?from_user_id=${transferFrom.user_id}&to_user_id=${transferToId}`);
      setTransferPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to build dry-run preview");
    } finally {
      setTransferLoading(false);
    }
  };

  const confirmTransferExecute = async () => {
    if (!window.confirm(`Transfer current operational ownership from "${transferFrom.name}" to "${transferPreview.to_user.name}"? Historical records will NOT change. This cannot be undone.`)) return;
    setTransferExecuting(true);
    try {
      const res = await api.post(`/admin/employee-transfer/execute`, {
        from_user_id: transferFrom.user_id,
        to_user_id: transferToId,
      });
      setTransferResult(res.data);
      toast.success("Ownership transfer complete");
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transfer failed");
    } finally {
      setTransferExecuting(false);
    }
  };

  const getRoleBadge = (role) => {
    const map = {
      admin: "bg-red-50 text-red-700 border-red-200",
      manager: "bg-blue-50 text-blue-700 border-blue-200",
      bdm: "bg-green-50 text-green-700 border-green-200",
      bde: "bg-yellow-50 text-yellow-700 border-yellow-200",
      customer: "bg-slate-100 text-slate-700 border-slate-200",
    };
    return map[role] || map.customer;
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64 p-8">
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="flex items-center"><h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>User Management</h1><HelpButton moduleKey="user_management" /></div>
            <p className="text-base text-slate-700">Create and manage team members</p>
          </div>
          <button data-testid="add-user-btn" onClick={() => setShowAdd(!showAdd)}
            className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium transition-colors duration-300 flex items-center gap-2">
            <UserPlus size={18} /> {showAdd ? 'Cancel' : 'Add User'}
          </button>
        </div>

        {showAdd && (
          <div data-testid="add-user-form" className="bg-white border border-slate-200 rounded-none p-8 mb-8 shadow-sm">
            <h3 className="text-xl font-semibold mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>Create New User</h3>
            <form onSubmit={handleAdd} className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Full Name</label>
                <input data-testid="new-user-name" type="text" value={newUser.name}
                  onChange={e => setNewUser({...newUser, name: e.target.value})}
                  className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Email</label>
                <input data-testid="new-user-email" type="email" value={newUser.email}
                  onChange={e => setNewUser({...newUser, email: e.target.value})}
                  className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Password</label>
                <input data-testid="new-user-password" type="password" value={newUser.password}
                  onChange={e => setNewUser({...newUser, password: e.target.value})}
                  className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" required minLength={6} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Role</label>
                <select data-testid="new-user-role" value={newUser.role}
                  onChange={e => setNewUser({...newUser, role: e.target.value})}
                  className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none">
                  {ROLES.map(r => <option key={r} value={r}>{r.toUpperCase()}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <button data-testid="submit-new-user" type="submit"
                  className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-8 py-3 font-medium transition-colors duration-300">
                  Create User
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? <div className="flex justify-center py-20"><div className="loading-spinner"></div></div> : (
          <div data-testid="users-table" className="bg-white border border-slate-200 rounded-none shadow-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-y border-slate-200">
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Name</th>
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Email</th>
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Role</th>
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Department</th>
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Org Role</th>
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500"><span className="inline-flex items-center">WhatsApp <HelpFieldIcon moduleKey="user_management" fieldKey="whatsapp_number" /></span></th>
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Change Role</th>
                  <th className="text-right p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-300">
                    <td className="p-4 font-medium">
                      {u.name}
                      {u.employee_id && <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">EMP {u.employee_id}</div>}
                    </td>
                    <td className="p-4 text-sm text-slate-600">{u.email}</td>
                    <td className="p-4">
                      <span className={`rounded-none px-2 py-1 text-xs font-bold uppercase tracking-wider border ${getRoleBadge(u.role)}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-slate-700">{u.department_name || <span className="text-slate-400">—</span>}</td>
                    <td className="p-4 text-sm text-slate-700">{u.org_role_name || <span className="text-slate-400">—</span>}</td>
                    <td className="p-4">
                      <input
                        data-testid={`user-wa-${u.user_id}`}
                        defaultValue={u.whatsapp_number || ""}
                        placeholder="91XXXXXXXXXX"
                        onBlur={async (e) => {
                          const v = e.target.value.trim();
                          if (v === (u.whatsapp_number || "")) return;
                          try {
                            await api.put(`${API}/users/${u.user_id}/whatsapp?requester_role=${user?.role || ""}`, { whatsapp_number: v });
                            toast.success("WhatsApp updated");
                            fetchUsers();
                          } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
                        }}
                        className="rounded-none border border-slate-300 focus:border-emerald-600 p-1.5 text-xs font-mono w-36 outline-none"
                      />
                    </td>
                    <td className="p-4">
                      <select value={u.role} onChange={e => changeRole(u.email, e.target.value)}
                        data-testid={`change-role-${u.user_id}`}
                        className="rounded-none border border-slate-300 text-sm p-2 outline-none">
                        {ROLES.map(r => <option key={r} value={r}>{r.toUpperCase()}</option>)}
                      </select>
                    </td>
                    <td className="p-4 text-right">
                      <button data-testid={`edit-org-${u.user_id}`} onClick={() => openOrg(u)}
                        className="p-2 hover:bg-blue-50 text-[#002FA7] transition-colors mr-1" title="Edit Org Details">
                        <IdentificationCard size={16} weight="bold" />
                      </button>
                      {user?.role === "admin" && (
                        <button data-testid={`transfer-ownership-${u.user_id}`} onClick={() => openTransfer(u)}
                          className="p-2 hover:bg-amber-50 text-amber-600 transition-colors mr-1" title="Transfer Ownership">
                          <ArrowsLeftRight size={16} weight="bold" />
                        </button>
                      )}
                      {user?.role === "admin" && u.role === "bdm" && (
                        <button data-testid={`manage-team-${u.user_id}`} onClick={() => setManageTeamBdm({ bdm_user_id: u.user_id, bdm_name: u.name })}
                          className="p-2 hover:bg-emerald-50 text-emerald-600 transition-colors mr-1" title="Manage Team">
                          <UsersThree size={16} weight="bold" />
                        </button>
                      )}
                      <button data-testid={`delete-user-${u.user_id}`} onClick={() => deleteUser(u)}
                        disabled={u.email === "admin@swiftechindia.com"}
                        className="p-2 hover:bg-red-50 text-red-500 transition-colors duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={u.email === "admin@swiftechindia.com" ? "Cannot delete primary admin" : "Delete user"}>
                        <Trash size={16} weight="bold" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && <div className="text-center py-12 text-slate-500">No users found</div>}
          </div>
        )}

        {orgUser && (
          <div className="fixed inset-0 z-50 bg-black/40 flex justify-end">
            <div data-testid="org-drawer" className="bg-white w-full max-w-xl h-full overflow-y-auto border-l border-slate-200 shadow-2xl">
              <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between z-10">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Org Details</div>
                  <h3 className="text-xl font-semibold" style={{fontFamily: 'Outfit, sans-serif'}}>{orgUser.name}</h3>
                  <div className="text-xs text-slate-500">{orgUser.email}</div>
                </div>
                <button data-testid="org-drawer-close" onClick={() => setOrgUser(null)} className="p-2 hover:bg-slate-100"><X size={20} /></button>
              </div>
              <div className="p-5 space-y-4">
                {[
                  ["Employee ID", "employee_id", "text"],
                  ["Mobile", "mobile", "tel"],
                  ["WhatsApp", "whatsapp", "tel"],
                  ["Branch", "branch", "text"],
                ].map(([lbl, key, type]) => (
                  <div key={key}>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">{lbl}</label>
                    <input data-testid={`org-${key}`} type={type} value={orgPatch[key] || ""} onChange={(e) => setOrgPatch({...orgPatch, [key]: e.target.value})}
                      className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-2.5 w-full outline-none text-sm" />
                  </div>
                ))}

                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Department</label>
                  <select data-testid="org-department" value={orgPatch.department_id || ""} onChange={(e) => {
                    const d = departments.find(x => x.department_id === e.target.value);
                    setOrgPatch({...orgPatch, department_id: e.target.value, department_name: d?.name || ""});
                  }} className="rounded-none bg-white border border-slate-300 p-2.5 w-full outline-none text-sm">
                    <option value="">— Not assigned —</option>
                    {departments.map(d => <option key={d.department_id} value={d.department_id}>{d.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Org Role</label>
                  <select data-testid="org-role" value={orgPatch.org_role_id || ""} onChange={(e) => {
                    const r = orgRoles.find(x => x.role_id === e.target.value);
                    setOrgPatch({...orgPatch, org_role_id: e.target.value, org_role_name: r?.name || ""});
                  }} className="rounded-none bg-white border border-slate-300 p-2.5 w-full outline-none text-sm">
                    <option value="">— Not assigned —</option>
                    {orgRoles
                      .filter(r => !orgPatch.department_id || r.department_id === orgPatch.department_id)
                      .map(r => <option key={r.role_id} value={r.role_id}>{r.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Reporting Manager</label>
                  <select data-testid="org-reporting" value={orgPatch.reporting_manager_id || ""} onChange={(e) => {
                    const u = users.find(x => x.user_id === e.target.value);
                    setOrgPatch({...orgPatch, reporting_manager_id: e.target.value, reporting_manager_name: u?.name || ""});
                  }} className="rounded-none bg-white border border-slate-300 p-2.5 w-full outline-none text-sm">
                    <option value="">— None —</option>
                    {users.filter(u => u.user_id !== orgUser.user_id).map(u => <option key={u.user_id} value={u.user_id}>{u.name} ({u.role})</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Status</label>
                    <select data-testid="org-status" value={orgPatch.status || "active"} onChange={(e) => setOrgPatch({...orgPatch, status: e.target.value})}
                      className="rounded-none bg-white border border-slate-300 p-2.5 w-full outline-none text-sm">
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Login Access</label>
                    <select data-testid="org-login" value={orgPatch.login_access ? "yes" : "no"} onChange={(e) => setOrgPatch({...orgPatch, login_access: e.target.value === "yes"})}
                      className="rounded-none bg-white border border-slate-300 p-2.5 w-full outline-none text-sm">
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>

                <button data-testid="org-save-btn" onClick={saveOrg} className="w-full rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-3 font-medium transition-colors">
                  Save Org Details
                </button>
              </div>
            </div>
          </div>
        )}

        {transferFrom && (
          <div className="fixed inset-0 z-50 bg-black/40 flex justify-end">
            <div data-testid="transfer-drawer" className="bg-white w-full max-w-2xl h-full overflow-y-auto border-l border-slate-200 shadow-2xl">
              <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between z-10">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-amber-600 font-bold">Transfer Ownership</div>
                  <h3 className="text-xl font-semibold" style={{fontFamily: 'Outfit, sans-serif'}}>From: {transferFrom.name}</h3>
                  <div className="text-xs text-slate-500">{transferFrom.email} · {transferFrom.role?.toUpperCase()}</div>
                </div>
                <button data-testid="transfer-drawer-close" onClick={closeTransfer} className="p-2 hover:bg-slate-100"><X size={20} /></button>
              </div>

              <div className="p-5 space-y-5">
                {!transferResult && (
                  <>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Replacement Employee</label>
                      <select data-testid="transfer-to-select" value={transferToId}
                        onChange={(e) => { setTransferToId(e.target.value); setTransferPreview(null); }}
                        className="rounded-none bg-white border border-slate-300 p-2.5 w-full outline-none text-sm">
                        <option value="">— Select replacement —</option>
                        {users.filter(u => u.user_id !== transferFrom.user_id).map(u => (
                          <option key={u.user_id} value={u.user_id}>{u.name} ({u.role})</option>
                        ))}
                      </select>
                    </div>

                    <button data-testid="transfer-run-preview-btn" onClick={runTransferPreview} disabled={transferLoading}
                      className="w-full rounded-none bg-slate-800 text-white hover:bg-slate-900 px-5 py-3 font-medium transition-colors disabled:opacity-50">
                      {transferLoading ? "Running Dry-Run..." : "Run Dry-Run Preview"}
                    </button>

                    {transferPreview && (
                      <div data-testid="transfer-preview-results" className="space-y-4">
                        <div className="bg-amber-50 border border-amber-200 p-3 text-sm">
                          <span className="font-bold">{transferPreview.from_user.name}</span> → <span className="font-bold">{transferPreview.to_user.name}</span>
                        </div>

                        <div>
                          <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Will Transfer (current ownership)</div>
                          <table className="w-full text-sm border border-slate-200">
                            <tbody>
                              {[
                                ["Contacts", "contacts"],
                                ["Leads", "leads"],
                                ["Active Deals", "active_deals"],
                                ["Open Tasks", "open_tasks"],
                                ["Follow-ups", "follow_ups"],
                                ["Product Leads", "product_leads"],
                                ["Enquiries", "enquiries"],
                                ["Customer Responsibilities", "customer_responsibilities"],
                              ].map(([label, key]) => (
                                <tr key={key} className="border-b border-slate-100">
                                  <td className="p-2">{label}</td>
                                  <td className="p-2 text-right font-mono font-bold" data-testid={`transfer-count-${key}`}>{transferPreview.transferring[key]}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div>
                          <div className="text-xs uppercase tracking-wider font-bold text-emerald-700 mb-2">Preserved — NOT Changed (historical)</div>
                          <table className="w-full text-sm border border-emerald-200">
                            <tbody>
                              {[
                                ["Historical Quotations/Estimations", "historical_quotations_estimations"],
                                ["Historical Proformas", "historical_proformas"],
                                ["Historical Invoices", "historical_invoices"],
                                ["Closed Deals", "closed_deals"],
                                ["Completed Tasks", "completed_tasks"],
                                ["Activities / Audit History", "activities_audit_history"],
                              ].map(([label, key]) => (
                                <tr key={key} className="border-b border-emerald-100">
                                  <td className="p-2">{label}</td>
                                  <td className="p-2 text-right font-mono font-bold text-emerald-700" data-testid={`transfer-preserved-${key}`}>
                                    {transferPreview.preserved_unchanged[key]} — NOT CHANGED
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <button data-testid="transfer-confirm-execute-btn" onClick={confirmTransferExecute} disabled={transferExecuting}
                          className="w-full rounded-none bg-red-600 text-white hover:bg-red-700 px-5 py-3 font-medium transition-colors disabled:opacity-50">
                          {transferExecuting ? "Transferring..." : "Confirm & Execute Transfer"}
                        </button>
                      </div>
                    )}
                  </>
                )}

                {transferResult && (
                  <div data-testid="transfer-execute-result" className="space-y-4">
                    <div className="bg-emerald-50 border border-emerald-200 p-4 text-emerald-800 font-medium">
                      {transferResult.message}
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Records Transferred</div>
                      <table className="w-full text-sm border border-slate-200">
                        <tbody>
                          {Object.entries(transferResult.record_counts).map(([key, val]) => (
                            <tr key={key} className="border-b border-slate-100">
                              <td className="p-2 capitalize">{key.replace(/_/g, " ")}</td>
                              <td className="p-2 text-right font-mono font-bold">{val}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="text-xs text-slate-500">Transfer ID: {transferResult.transfer_id}</div>
                    <button data-testid="transfer-close-result-btn" onClick={closeTransfer}
                      className="w-full rounded-none bg-slate-800 text-white hover:bg-slate-900 px-5 py-3 font-medium transition-colors">
                      Done
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {manageTeamBdm && (
          <ManageTeamModal bdm={manageTeamBdm} onClose={() => setManageTeamBdm(null)} />
        )}
      </div>
    </div>
  );
};

export default UserManagement;
